/**
 * PensumView -- el pensum de los agentes, en cascada (PENSUM-PANEL-CASCADA-T1; Josse 21-oct, SI de Ares #32039).
 *
 * AUDIENCIA: Josse o un admin de Nexo, en el panel, en escritorio o celular.
 *
 * Josse: "entre menos texto inicial en el panel mejor". Arriba solo el buscador y la lista de agentes con su avance
 * (n de N); al abrir un agente salen sus skills, y al abrir una skill sus unidades. La unidad dice su estado, el
 * criterio, la evidencia que pide y la que ya tiene, con enlace a lo que existe (PR, nodo, captura).
 *
 * Se usa <details>: abrir y cerrar no necesita JavaScript, el teclado lo maneja solo y el lector de pantalla dice si
 * esta abierto. Con una busqueda puesta, todo lo que calza queda abierto.
 *
 * FAIL-OPEN: sin la ruta (A la publica) dice que el pensum aun no esta guardado; no pinta ceros.
 *
 * @module presentation/views/PensumView
 */

import { setPageTitle } from '../components/NavBar.js';
import { PensumAgentesApiClient } from '../../infrastructure/api/PensumAgentesApiClient.js';
import { normalizarPensumAgentes, filtrarPensum, textoDeEstado } from '../../application/pensum/pensumCascada.js';
import { normalizarPensumNexo, textoDeEstadoNexo, semaforo } from '../../application/pensum/pensumNexo.js';

const C = { borde: 'var(--border-default)', texto: 'var(--text-strong)', suave: 'var(--text-secondary)', ok: 'var(--accent-green-text)', alerta: 'var(--accent-yellow)', enlace: 'var(--accent-blue)' };

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';

const NOMBRES = {
  'ARGY-PANEL-LOCAL-001': 'Panel (P)',
  'ARGY-DESKTOP-LOCAL-001': 'Escritorio (D)',
  'ARGY-API-LOCAL-001': 'API (A)',
  'ARGY-Q-LOCAL-001': 'Calidad (Q)'
};

const COLOR_ESTADO = { aprobada: C.ok, en_practica: C.alerta };

const FALTA = {
  NO_PUBLICADO: 'El pensum de los agentes todavia no esta guardado en la base. Cuando la API lo publique, aparece aqui.',
  PROHIBIDO: 'El pensum es solo para administradores de Nexo.',
  SIN_SESION: 'Tu sesion vencio: vuelve a entrar.'
};

function unidadHTML(u, abierto) {
  const color = COLOR_ESTADO[u.estado] || C.suave;
  const evidencia = u.intentos.flatMap((i) => i.evidencia.map((e) => {
    const etiqueta = esc([e.tipo, e.ref].filter(Boolean).join(' '));
    return e.url ? `<li><a href="${esc(e.url)}" target="_blank" rel="noopener" style="color:${C.enlace}">${etiqueta}</a></li>` : `<li>${etiqueta}</li>`;
  })).join('');
  return `<details data-pensum-unidad="${esc(u.id)}" ${abierto ? 'open' : ''} style="border-top:1px solid ${C.borde};padding:8px 0">
    <summary style="cursor:pointer;display:flex;flex-wrap:wrap;gap:4px 10px;align-items:baseline">
      <span style="flex:1 1 180px;min-width:0;overflow-wrap:anywhere">${esc(u.titulo || u.id)}</span>
      <span data-pensum-estado style="color:${color};white-space:nowrap">${esc(textoDeEstado(u.estado))}</span>
    </summary>
    <dl style="margin:8px 0 0;display:grid;grid-template-columns:minmax(0,1fr);gap:4px">
      <dt style="color:${C.suave}">Que se pide</dt><dd style="margin:0">${esc(u.criterio || 'Sin criterio escrito')}</dd>
      <dt style="color:${C.suave}">Evidencia</dt><dd style="margin:0">${esc(u.evidenciaRequerida || 'Sin evidencia pedida')}</dd>
      ${u.prerrequisitos.length ? `<dt style="color:${C.suave}">Antes</dt><dd style="margin:0">${esc(u.prerrequisitos.join(', '))}</dd>` : ''}
      <dt style="color:${C.suave}">Lo que ya tiene</dt><dd style="margin:0">${evidencia ? `<ul style="margin:0;padding-left:18px">${evidencia}</ul>` : 'Nada todavia'}</dd>
    </dl>
  </details>`;
}

function agenteHTML(a, abierto) {
  const skills = a.skills.map((s) => {
    return `<details data-pensum-skill="${esc(s.skill)}" ${abierto ? 'open' : ''} style="margin-top:6px;padding-left:12px;border-left:2px solid ${C.borde}">
      <summary style="cursor:pointer;display:flex;flex-wrap:wrap;gap:4px 10px">
        <span style="flex:1 1 160px;min-width:0;overflow-wrap:anywhere">${esc(s.skill || 'Sin skill')}</span>
        <span data-pensum-skill-avance style="color:${C.suave};white-space:nowrap">${s.aprobadas} de ${s.total}</span>
      </summary>
      ${s.unidades.map((u) => unidadHTML(u, abierto)).join('')}
    </details>`;
  }).join('');
  return `<details data-pensum-agente="${esc(a.agente)}" ${abierto ? 'open' : ''} style="border:1px solid ${C.borde};border-radius:8px;padding:10px 12px;margin-top:8px">
    <summary style="cursor:pointer;display:flex;flex-wrap:wrap;gap:4px 12px;align-items:baseline">
      <strong style="flex:1 1 160px;min-width:0;font-size:15px">${esc(NOMBRES[a.agente] || a.agente)}</strong>
      <span data-pensum-avance style="white-space:nowrap">${a.aprobadas} de ${a.total} aprobadas</span>
    </summary>
    <a href="#/agente/${encodeURIComponent(a.agente)}" data-pensum-ficha style="display:inline-block;margin-top:6px;font-size:14px;color:var(--accent-blue)">Ver su ficha</a>
    ${skills || `<p style="margin:8px 0 0;color:${C.suave}">Sin unidades todavia.</p>`}
  </details>`;
}

// ─── PENSUM-NEXO-PANEL-T1: el pensum comun de Nexo arriba y lo que cada agente tiene de el ───────────────────────

const COLOR_SEMAFORO = { verde: C.ok, amarillo: C.alerta, gris: C.suave };
const COLOR_NEXO = { citada: C.alerta, verificada: C.ok };

const FALTA_NEXO = {
  NO_PUBLICADO: 'El pensum comun de Nexo todavia no esta en la base. Cuando la API lo publique, aparece aqui.',
  PROHIBIDO: 'El pensum comun es solo para administradores de Nexo.',
  SIN_SESION: 'Tu sesion vencio: vuelve a entrar.'
};

function disparadorTexto(u) {
  const partes = [];
  if (u.palabras.length) partes.push('palabras: ' + u.palabras.join(', '));
  if (u.rutas.length) partes.push('archivos: ' + u.rutas.join(', '));
  return partes.join(' · ') || (u.tipo === 'base' ? 'Siempre (unidad base)' : 'Sin disparador escrito');
}

function unidadNexoHTML(u, comunPorCodigo) {
  const def = comunPorCodigo.get(u.codigo);
  const cita = u.ultimaCita
    ? `bitacora #${esc(u.ultimaCita.bitacoraId ?? '?')}${u.ultimaCita.fecha ? ' · ' + esc(u.ultimaCita.fecha) : ''}`
    : 'Nunca';
  return `<details data-pensum-nexo-unidad="${esc(u.codigo)}" style="border-top:1px solid ${C.borde};padding:8px 0">
    <summary style="cursor:pointer;display:flex;flex-wrap:wrap;gap:4px 10px;align-items:baseline">
      <span style="flex:1 1 180px;min-width:0;overflow-wrap:anywhere">${esc(u.titulo || u.codigo)}</span>
      <span data-pensum-nexo-estado style="color:${COLOR_NEXO[u.estado] || C.suave};white-space:nowrap">${esc(textoDeEstadoNexo(u.estado))}</span>
    </summary>
    <dl style="margin:8px 0 0;display:grid;grid-template-columns:minmax(0,1fr);gap:4px">
      <dt style="color:${C.suave}">Cuando se lee</dt><dd style="margin:0;overflow-wrap:anywhere">${esc(def ? disparadorTexto(def) : 'Unidad que no esta en el pensum comun')}</dd>
      ${def && def.dondeVive ? `<dt style="color:${C.suave}">Donde vive</dt><dd style="margin:0;overflow-wrap:anywhere">${esc(def.dondeVive)}</dd>` : ''}
      <dt style="color:${C.suave}">Ultima cita</dt><dd style="margin:0" data-pensum-nexo-cita>${cita}</dd>
    </dl>
  </details>`;
}

function agenteNexoHTML(a, comunPorCodigo) {
  const s = semaforo(a);
  return `<details data-pensum-nexo-agente="${esc(a.agente)}" style="border:1px solid ${C.borde};border-radius:8px;padding:10px 12px;margin-top:8px">
    <summary style="cursor:pointer;display:flex;flex-wrap:wrap;gap:4px 12px;align-items:baseline">
      <span aria-hidden="true" style="color:${COLOR_SEMAFORO[s] || C.suave}">&#9679;</span>
      <strong style="flex:1 1 140px;min-width:0;font-size:15px">${esc(NOMBRES[a.agente] || a.agente)}</strong>
      <span data-pensum-nexo-avance style="white-space:nowrap">${a.citadas} de ${a.total} citadas · ${a.verificadas} verificada${a.verificadas === 1 ? '' : 's'}</span>
    </summary>
    ${a.unidades.map((u) => unidadNexoHTML(u, comunPorCodigo)).join('') || `<p style="margin:8px 0 0;color:${C.suave}">Sin unidades todavia.</p>`}
  </details>`;
}

/** Arriba de la vista: el pensum comun en una linea y una fila por agente. Nunca lanza; sin la ruta, lo dice. */
async function pintarPensumNexo($host) {
  if (!$host) return;
  const res = await PensumAgentesApiClient.getNexo();
  if (!$host.isConnected) return;
  if (!res.ok) {
    $host.innerHTML = `<div data-pensum-nexo-falta style="color:${C.suave};border:1px dashed ${C.borde};border-radius:8px;padding:10px 12px">${esc(FALTA_NEXO[res.motivo] || 'No pudimos traer el pensum comun ahora.')}</div>`;
    return;
  }
  const { comun, agentes } = normalizarPensumNexo(res.data);
  if (!comun) {
    $host.innerHTML = `<div data-pensum-nexo-falta style="color:${C.suave};border:1px dashed ${C.borde};border-radius:8px;padding:10px 12px">El pensum comun de Nexo esta en construccion: todavia no tiene unidades.</div>`;
    return;
  }
  const porCodigo = new Map(comun.unidades.map((u) => [u.codigo, u]));
  const base = comun.unidades.filter((u) => u.tipo === 'base').length;
  $host.innerHTML = `<h3 style="margin:0 0 4px;font-size:16px">Pensum comun de Nexo</h3>
    <div data-pensum-nexo-resumen style="color:${C.suave}">${esc(comun.code)} ${esc(comun.version)}${comun.estado ? ' · ' + esc(comun.estado) : ''} · ${base} unidades base y ${comun.unidades.length - base} por tema</div>
    ${agentes.map((a) => agenteNexoHTML(a, porCodigo)).join('') || `<p style="margin:8px 0 0;color:${C.suave}">Ningun agente lo ha citado todavia.</p>`}`;
}

/**
 * Punto de entrada de la vista.
 * @param {HTMLElement} container
 */
export async function renderPensum(container) {
  setPageTitle?.('Pensum');
  container.innerHTML = `
    <style>
      /* El summary en flex pierde el triangulo: se pone a mano para que se note que abre. */
      [data-pensum-lista] summary { list-style: none; }
      [data-pensum-lista] summary::-webkit-details-marker { display: none; }
      [data-pensum-lista] summary::before { content: '\\25B8'; color: ${C.suave}; width: 1em; flex: 0 0 1em; }
      [data-pensum-lista] details[open] > summary::before { content: '\\25BE'; }
      [data-pensum-lista] summary:focus-visible { outline: 2px solid ${C.enlace}; outline-offset: 2px; }
    </style>
    <div class="detail-section" style="font-size:14px;color:${C.texto}">
      ${regresoHtml()}
      <h2 style="margin:4px 0 10px;font-size:18px">Pensum</h2>
      <div id="nx-pensum-nexo" data-pensum-nexo data-pensum-lista style="margin-bottom:14px"><div style="color:${C.suave}">Cargando el pensum comun...</div></div>
      <label for="nx-pensum-buscar" style="display:block;color:${C.suave};margin-bottom:4px">Buscar agente, skill o unidad</label>
      <input id="nx-pensum-buscar" data-pensum-buscar type="search" autocomplete="off"
        style="width:100%;max-width:420px;box-sizing:border-box;font-size:14px;padding:8px 10px;border-radius:6px;border:1px solid ${C.borde};background:var(--bg-canvas);color:${C.texto}">
      <div id="nx-pensum-lista" data-pensum-lista style="margin-top:8px"><div style="color:${C.suave}">Cargando...</div></div>
    </div>`;
  const $lista = container.querySelector('#nx-pensum-lista');
  const $buscar = container.querySelector('#nx-pensum-buscar');
  pintarPensumNexo(container.querySelector('#nx-pensum-nexo'));

  const res = await PensumAgentesApiClient.get();
  if (!res.ok) {
    $lista.innerHTML = `<div data-pensum-falta style="color:${C.suave};border:1px dashed ${C.borde};border-radius:8px;padding:10px 12px">${esc(FALTA[res.motivo] || 'No pudimos traer el pensum ahora.')}</div>`;
    $buscar.disabled = true;
    return;
  }
  const agentes = normalizarPensumAgentes(res.data);

  const pintar = () => {
    const q = $buscar.value;
    if (!agentes.length) { $lista.innerHTML = `<div style="color:${C.suave}">Ningun agente tiene pensum todavia.</div>`; return; }
    const vistos = filtrarPensum(agentes, q);
    $lista.innerHTML = vistos.length
      ? vistos.map((a) => agenteHTML(a, !!q.trim())).join('')
      : `<div data-pensum-sin-resultado style="color:${C.suave}">Nada calza con "${esc(q)}".</div>`;
  };
  $buscar.addEventListener('input', pintar);
  pintar();
}
